import React, { useEffect, useMemo, useState } from 'react';
import { FiUsers, FiUser, FiSearch, FiHash, FiBriefcase } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { agentAPI } from '../../services/api';
import { AGENT_STATUS_OPTIONS, getAgentStatusMeta } from '../../utils/agentStatus';
import toast from 'react-hot-toast';

const LineTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    let active = true;
    const fetchTeam = async () => {
      try {
        const { data } = await agentAPI.getDownlines();
        if (active) setTeamMembers(Array.isArray(data) ? data : []);
      } catch (error) {
        if (active) toast.error('ไม่สามารถดึงข้อมูลทีมได้');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchTeam();
    return () => { active = false; };
  }, []);

  const filteredMembers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return teamMembers.filter((member) => {
      const searchable = `${member.firstName || ''} ${member.lastName || ''} ${member.nickname || ''} ${member.agentCode || ''}`.toLowerCase();
      const status = getAgentStatusMeta(member.agentStatus).status;
      return (!normalizedSearch || searchable.includes(normalizedSearch)) &&
        (filterStatus === 'all' || status === filterStatus);
    });
  }, [teamMembers, searchTerm, filterStatus]);

  const activeCount = teamMembers.filter((member) => getAgentStatusMeta(member.agentStatus).status === 'active').length;
  const inactiveCount = teamMembers.length - activeCount;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">ทีมของฉัน</h1>
        <p className="text-gray-600">ดูข้อมูลและสถานภาพของสมาชิกในทีม</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'สมาชิกทั้งหมด', value: teamMembers.length, color: 'text-blue-600' },
          { label: 'มีผลบังคับ', value: activeCount, color: 'text-emerald-600' },
          { label: 'สิ้นผล', value: inactiveCount, color: 'text-red-600' },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-lg shadow-sm border border-gray-100 p-5 flex items-center">
            <SafeIcon icon={FiUsers} className={`w-7 h-7 ${item.color}`} />
            <div className="ml-4">
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="text-2xl font-bold text-gray-900">{item.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="ค้นหาชื่อ ชื่อเล่น หรือรหัสตัวแทน" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500">
          <option value="all">สถานะทั้งหมด</option>
          {AGENT_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-3">
          {filteredMembers.map((member) => {
            const statusMeta = getAgentStatusMeta(member.agentStatus);
            return (
              <div key={member.id} className={`bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-gray-100 border-l-4 ${statusMeta.border} rounded-lg shadow-sm`}>
                <div className="flex items-center space-x-4 min-w-0">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiUser} className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{member.firstName} {member.lastName}</h4>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                      <span className="flex items-center"><SafeIcon icon={FiHash} className="mr-1" />{member.agentCode || '-'}</span>
                      <span className="flex items-center"><SafeIcon icon={FiBriefcase} className="mr-1" />{member.position || 'ตัวแทน'}</span>
                    </div>
                  </div>
                </div>
                <span className={`self-start sm:self-auto px-2.5 py-1 text-xs font-medium rounded-full ${statusMeta.badge}`}>{statusMeta.label}</span>
              </div>
            );
          })}
          {filteredMembers.length === 0 && (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-lg py-12 text-center text-gray-500">ไม่พบตัวแทนตามเงื่อนไขที่เลือก</div>
          )}
        </div>
      )}
    </div>
  );
};

export default LineTeam;
